import React from "react";
import { Info } from "lucide-react";

import { STATS_TEXT } from "./catalogStatsText";

const ICON_SIZE = 12;
const SKELETON_TILES = 4;

/** A section head with the "Rough" tag that every estimate carries, and its explanation as the tooltip. */
export function RoughHead({ label, hint = STATS_TEXT.roughHint }) {
  return (
    <div className="le-mhead">
      <span className="le-label">{label}</span>
      <span className="le-tag le-tag--warn" title={hint}>{STATS_TEXT.rough}</span>
    </div>
  );
}

/**
 * One figure: its label, the value as a compact chip, a meter when it is a percent, and the one-line reading. The tooltip
 * (the info glyph and the whole tile) says how it is computed.
 */
export function StatTile({ tile }) {
  const { label, value, percent, explain, how } = tile;
  return (
    <li className="le-stat" title={how}>
      <span className="le-stat-head">
        <span className="le-stat-label">{label}</span>
        <Info size={ICON_SIZE} className="le-stat-info" aria-hidden="true" />
      </span>
      <span className="le-stat-value">{value}</span>
      {typeof percent === "number" && (
        <span className="le-meter" role="img" aria-label={`${label}: ${percent}%`}>
          <span className="le-meter-fill" style={{ width: `${percent}%` }} />
        </span>
      )}
      <span className="le-stat-explain">{explain}</span>
      <span className="sr-only">{how}</span>
    </li>
  );
}

/** The tiles as a wrapping grid; `children` may add tiles that load on their own. */
export function StatGrid({ tiles, label, children }) {
  return (
    <ul className="le-stats" aria-label={label}>
      {tiles.map((t) => <StatTile key={t.key} tile={t} />)}
      {children}
    </ul>
  );
}

/** A tile-shaped placeholder: the final geometry (label line, value chip, meter, explanation line) while the figures load. */
export function SkeletonTile() {
  return (
    <li className="le-stat le-stat--skel" aria-hidden="true">
      <span className="le-stat-skel le-stat-skel--label" />
      <span className="le-stat-skel le-stat-skel--value" />
      <span className="le-meter"><span className="le-meter-fill le-meter-fill--skel" /></span>
      <span className="le-stat-skel le-stat-skel--explain" />
    </li>
  );
}

export function SkeletonGrid({ count = SKELETON_TILES }) {
  return <ul className="le-stats">{Array.from({ length: count }, (_, i) => <SkeletonTile key={i} />)}</ul>;
}

/** One cause's share at the end: name, a meter and the percent; the learned cause is marked. */
export function ShareRow({ share }) {
  return (
    <li className={`le-share${share.learned ? " le-share--learned" : ""}`}>
      <span className="le-share-name">
        {share.name}
        {share.learned && <span className="le-tag">{STATS_TEXT.causes.learned}</span>}
      </span>
      <span className="le-meter" role="img" aria-label={`${share.name}: ${share.percent}%`}>
        <span className="le-meter-fill" style={{ width: `${share.percent}%` }} />
      </span>
      <span className="le-share-pct">{`${share.percent}%`}</span>
    </li>
  );
}
